"use client";

import { useMemo } from "react";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import { CommunitySearchInput } from "./CommunitySearchInput";
import { CommunityFilterPills } from "./CommunityFilterPills";
import { CommunityFilterDropdown } from "./CommunityFilterDropdown";
import { BasedInFilterDropdown } from "./BasedInFilterDropdown";
import { DropdownOption } from "./DropdownOption";
import { SORT_OPTIONS } from "@/lib/community/constants";
import type { RegionCode } from "@/lib/community/constants";
import type {
  PublicCommunityCategory,
  PublicCommunityCountry,
  PublicCommunityFilterCollection,
  PublicCommunityRegion,
} from "@/lib/community/types";
import type { QuickFilterValue } from "./QuickFilterChips";
import type { CountryCode } from "./CountryFilterChips";

interface CommunityFilterBarProps {
  searchQuery: string;
  quickFilter: QuickFilterValue;
  selectedRegion: RegionCode | null;
  selectedCountry: CountryCode | null;
  selectedCategory: string | null;
  selectedCollection: string | null;
  categories: PublicCommunityCategory[];
  collections: PublicCommunityFilterCollection[];
  regions: PublicCommunityRegion[];
  countries: PublicCommunityCountry[];
  onSearchChange: (value: string) => void;
  onSearchFocus?: () => void;
  onQuickFilterChange: (value: QuickFilterValue) => void;
  onBasedInChange: (
    type: "region" | "country" | null,
    code: RegionCode | CountryCode | null,
  ) => void;
  onCategoryChange: (id: string | null) => void;
  onCollectionChange: (id: string | null) => void;
  onClearFilters: () => void;
  showActivePills?: boolean;
  className?: string;
  bordered?: boolean;
}

export function CommunityFilterBar({
  searchQuery,
  quickFilter,
  selectedRegion,
  selectedCountry,
  selectedCategory,
  selectedCollection,
  categories,
  collections,
  regions,
  countries,
  onSearchChange,
  onSearchFocus,
  onQuickFilterChange,
  onBasedInChange,
  onCategoryChange,
  onCollectionChange,
  onClearFilters,
  showActivePills = true,
  className,
  bordered = true,
}: CommunityFilterBarProps) {
  const activeFilters = useMemo(() => {
    const filters: { label: string; onRemove: () => void }[] = [];

    if (searchQuery) {
      filters.push({
        label: `Search: ${searchQuery}`,
        onRemove: () => onSearchChange(""),
      });
    }

    if (selectedCategory) {
      const name = categories.find(
        (c) => c.internal_id === selectedCategory,
      )?.name;
      if (name) {
        filters.push({
          label: `Category: ${name}`,
          onRemove: () => onCategoryChange(null),
        });
      }
    }

    if (selectedCollection) {
      const name = collections.find(
        (c) => c.collection.internal_id === selectedCollection,
      )?.collection.name;
      if (name) {
        filters.push({
          label: `Collection: ${name}`,
          onRemove: () => onCollectionChange(null),
        });
      }
    }

    if (selectedRegion) {
      const name =
        regions.find((r) => r.code === selectedRegion)?.name ?? selectedRegion;
      filters.push({
        label: `Based in: ${name}`,
        onRemove: () => onBasedInChange(null, null),
      });
    }

    if (selectedCountry) {
      const name =
        countries.find((c) => c.code === selectedCountry)?.name ?? selectedCountry;
      filters.push({
        label: `Based in: ${name}`,
        onRemove: () => onBasedInChange(null, null),
      });
    }

    if (quickFilter) {
      const label =
        SORT_OPTIONS.find((o) => o.value === quickFilter)?.label ?? quickFilter;
      filters.push({
        label,
        onRemove: () => onQuickFilterChange(null),
      });
    }

    return filters;
  }, [
    searchQuery,
    selectedCategory,
    selectedCollection,
    selectedRegion,
    selectedCountry,
    quickFilter,
    categories,
    collections,
    regions,
    countries,
    onSearchChange,
    onCategoryChange,
    onCollectionChange,
    onBasedInChange,
    onQuickFilterChange,
  ]);

  const sortOptions = useMemo(
    () => [
      { value: null as null, label: "Recommended" },
      ...SORT_OPTIONS.filter((o) => o.value !== "default").map((o) => ({
        ...o,
        value: o.value as Exclude<QuickFilterValue, null>,
      })),
    ],
    []
  );

  const sortLabel = useMemo(
    () => sortOptions.find((o) => o.value === quickFilter)?.label ?? "Sort by",
    [sortOptions, quickFilter]
  );

  const categoryLabel = useMemo(
    () =>
      categories.find((c) => c.internal_id === selectedCategory)?.name ??
      "Category",
    [categories, selectedCategory]
  );

  const collectionLabel = useMemo(
    () =>
      collections.find(
        (c) => c.collection.internal_id === selectedCollection,
      )?.collection.name ?? "Featured collection",
    [collections, selectedCollection]
  );

  const basedInLabel = useMemo(() => {
    if (selectedRegion) {
      return (
        regions.find((r) => r.code === selectedRegion)?.name ?? selectedRegion
      );
    }
    if (selectedCountry) {
      return (
        countries.find((c) => c.code === selectedCountry)?.name ??
        selectedCountry
      );
    }
    return "Based in";
  }, [regions, countries, selectedRegion, selectedCountry]);

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <Container size="xl" className={className}>
      <div className="w-full">
        <div
          className={cn(
            "flex flex-col gap-4 rounded-2xl bg-brand-green-soft/25 p-4 sm:p-5",
            bordered &&
              "border border-brand-green-line/70 shadow-[0_10px_28px_rgb(28_25_23/0.05)]",
          )}
        >
          {/* Search input */}
          <CommunitySearchInput
            inputId="community-search"
            value={searchQuery}
            onChange={onSearchChange}
            onFocus={onSearchFocus}
            placeholder="Search brands, products, categories..."
            size="default"
            className="w-full"
          />

          {/* Filter triggers */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <CommunityFilterDropdown
              label={sortLabel}
              active={!!quickFilter}
              align="left"
            >
              <div className="grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto">
                {sortOptions.map((option) => (
                  <DropdownOption
                    key={option.value ?? "all"}
                    selected={
                      quickFilter === option.value ||
                      (option.value === null && !quickFilter)
                    }
                    onClick={() => onQuickFilterChange(option.value)}
                  >
                    {option.label}
                  </DropdownOption>
                ))}
              </div>
            </CommunityFilterDropdown>

            <BasedInFilterDropdown
              label={basedInLabel}
              selectedRegion={selectedRegion}
              selectedCountry={selectedCountry}
              regions={regions}
              countries={countries}
              onChange={onBasedInChange}
            />

            <CommunityFilterDropdown
              label={categoryLabel}
              active={!!selectedCategory}
              align="left"
            >
              <div className="grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto">
                <DropdownOption
                  selected={!selectedCategory}
                  onClick={() => onCategoryChange(null)}
                >
                  All categories
                </DropdownOption>
                {categories.map((category) => (
                  <DropdownOption
                    key={category.internal_id}
                    selected={selectedCategory === category.internal_id}
                    onClick={() => onCategoryChange(category.internal_id)}
                  >
                    {category.name}
                  </DropdownOption>
                ))}
              </div>
            </CommunityFilterDropdown>

            <CommunityFilterDropdown
              label={collectionLabel}
              active={!!selectedCollection}
            >
              <div className="grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto">
                <DropdownOption
                  selected={!selectedCollection}
                  onClick={() => onCollectionChange(null)}
                >
                  All collections
                </DropdownOption>
                {collections.map(({ collection }) => (
                  <DropdownOption
                    key={collection.internal_id}
                    selected={selectedCollection === collection.internal_id}
                    onClick={() => onCollectionChange(collection.internal_id)}
                  >
                    {collection.name}
                  </DropdownOption>
                ))}
              </div>
            </CommunityFilterDropdown>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="col-span-2 justify-self-end rounded px-2 py-1.5 text-sm font-medium text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 sm:ml-auto"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {showActivePills && (
          <CommunityFilterPills
            filters={activeFilters}
            onClearAll={onClearFilters}
          />
        )}
      </div>
    </Container>
  );
}
